import formatFilters from '@/utils/formatFilters'
import ADVANCED_FILTERS_TYPES from '@/constants/enums/ADVANCED_FILTERS_TYPES'

/**
 * Builds the request bodies for POST /quotes/kanban, POST /quotes/cards, and
 * POST /quotes/count.
 *
 * The kanban and cards endpoints answer 422 for the keys the table view sends,
 * and neither accepts the flat `statusesIn` parameter: the board selects its
 * columns with `statuses`, the card view goes through `filters.quote.statuses`
 * like any other filter. Both builders fold the status pills into that same
 * `filters.quote.statuses` shape through `foldStatusPills` below, so whichever
 * view issues the request, the persisted query log always carries the user's
 * full pill selection and getFilterStatusSetting can read it back the same way.
 *
 * The count endpoint is different: it accepts the flat `statusesIn` as-is, so
 * buildRecordCountRequest below skips foldStatusPills entirely.
 */

const STATUSES_FILTER_KEY = 'filters[quote][statuses]'

const KANBAN_PROHIBITED_KEYS = ['fields', 'statusesIn', 'page', 'per_page', 'with_pagination']
const CARDS_PROHIBITED_KEYS = ['fields', 'statusesIn']

type FlatFilters = Record<string, any>

type KanbanOptions = {
  statuses: string[],
  limit: number,
  offset: number,
}

type FoldedRequest = {
  payload: FlatFilters,
  isEmptyResult: boolean,
}

const omitKeys = (filters: FlatFilters, keys: string[]): FlatFilters => {
  return Object.fromEntries(
    Object.entries(filters || {}).filter(([key]) => !keys.includes(key)),
  )
}

const toSlugList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter(Boolean)
  }

  if (typeof value === 'string' && value.length) {
    return value.split('|').filter(Boolean)
  }

  return []
}

// Folds the flat status pill selection (`statusesIn`) into `filters.quote.statuses`,
// the only shape both /quotes/kanban and /quotes/cards accept for a status filter.
// The pills intersect with an advanced `is` filter and subtract an advanced `no_is`
// filter, mirroring how the backend combines the two once they share one key.
//
// The pills and the advanced status filter share that one key, so an empty
// intersection cannot be expressed in the request: an empty value reads as "no
// filter" and would return everything. Callers must skip the request entirely
// when `isEmptyResult` is true instead of sending it.
const foldStatusPills = (filters: FlatFilters, payload: FlatFilters): FoldedRequest => {
  const pillsStatuses = toSlugList(filters?.statusesIn)

  if (!pillsStatuses.length) {
    return { payload, isEmptyResult: false }
  }

  const advancedOption = filters?.[STATUSES_FILTER_KEY]?.option
  const advancedStatuses = toSlugList(filters?.[STATUSES_FILTER_KEY]?.value)

  let statuses: string[] = pillsStatuses

  if (advancedStatuses.length) {
    if (advancedOption === ADVANCED_FILTERS_TYPES.is) {
      statuses = pillsStatuses.filter((slug) => advancedStatuses.includes(slug))
    }
    else if (advancedOption === ADVANCED_FILTERS_TYPES.isNot) {
      statuses = pillsStatuses.filter((slug) => !advancedStatuses.includes(slug))
    }
  }

  if (!statuses.length) {
    return { payload, isEmptyResult: true }
  }

  return {
    payload: {
      ...payload,
      filters: {
        ...(payload.filters || {}),
        quote: {
          ...(payload.filters?.quote || {}),
          statuses: {
            option: ADVANCED_FILTERS_TYPES.is,
            value: statuses.join('|'),
          },
        },
      },
    },
    isEmptyResult: false,
  }
}

export const buildKanbanRequest = (filters: FlatFilters, { statuses, limit, offset }: KanbanOptions): FoldedRequest => {
  const payload = {
    ...formatFilters.toObject(omitKeys(filters, KANBAN_PROHIBITED_KEYS)),
    statuses,
    limit,
    offset,
  }

  // Folding the pills into filters.quote.statuses here is safe even though `statuses`
  // above is only the handful of columns this particular lazy-load request covers,
  // not the user's full pill selection: the requested columns are always a subset of
  // the folded pills, because a status outside the pill selection is not a column on
  // the board at all. The backend applies both, so the intersection - and therefore
  // the records returned - is unchanged. What changes is the persisted query log,
  // which now carries the full status selection instead of nothing, so the restore
  // path (getFilterStatusSetting) has something to read back after a reload.
  return foldStatusPills(filters, payload)
}

export const buildCardsRequest = (filters: FlatFilters): FoldedRequest => {
  const payload = formatFilters.toObject(omitKeys(filters, CARDS_PROHIBITED_KEYS))

  return foldStatusPills(filters, payload)
}

// POST /quotes/count answers 422 for the same pagination/sorting keys as the
// other endpoints, plus persistFilters and filterViewId: unlike the board's own
// load, a count check must never rewrite the persisted query log. This app
// spells sorting sort_by/order in its filter bag, and camelCase sortBy is the
// endpoint's own rejected spelling, so both are stripped alongside order.
const COUNT_PROHIBITED_KEYS = [
  'fields', 'page', 'per_page', 'with_pagination', 'sortBy', 'sort_by', 'order',
  'persistFilters', 'filterViewId',
]

// Builds the request body for POST /quotes/count, the kanban board's own total
// for the active filters - the number /quotes/all would paginate for the same
// user and filters, including record-access restrictions.
//
// Unlike buildKanbanRequest and buildCardsRequest, this endpoint accepts the
// flat `statusesIn` pill selection directly, so it is left untouched instead of
// being folded into filters.quote.statuses - that folding exists only because
// the kanban and cards endpoints reject the flat key.
export const buildRecordCountRequest = (filters: FlatFilters): FlatFilters => {
  return formatFilters.toObject(omitKeys(filters, COUNT_PROHIBITED_KEYS))
}
